import Entity from "@/entity/OperationEntity";
import {disassemble} from "es-hangul";
import axios from "@/composables/Axios";
import {changeFormat, endDateInit, startDateInit} from "@/components/tacho/common/CommonFunc";
import tachoEntity from "@/entity/TachoEntity";
import dayjs from "dayjs";
import {getOfficeShiftStartHours} from "@/components/tacho/searchbar/search-utils";

export const multiDateRangeTypes = {
    day: 'day',
    month: 'month',
    custom: 'custom',
};

export const carTypeGroups = {
    company: 'company',
    individual: 'individual',
};

export const carTypeGroupLabels = {
    [carTypeGroups.company]: '법인',
    [carTypeGroups.individual]: '개인',
};

export function carTypeToCarTypeGroup(carType) {
    const carTypeNum = parseInt(carType);
    const foundItem = [
        {
            group: carTypeGroups.company,
            types: [21, 71]
        },
        {
            group: carTypeGroups.individual,
            types: [22, 72]
        }
    ].find((item) => {
        if (item.types.indexOf(carTypeNum) !== -1) {
            return true;
        }

        return false;
    });

    if (foundItem) {
        return foundItem.group;
    }

    return null;
}

export function carTypeToCarTypeGroupLabel(carType) {
    const carTypeGroup = carTypeToCarTypeGroup(carType);
    if (carTypeGroup) {
        return carTypeGroupLabels[carTypeGroup];
    }

    return '없음';
}

export const searchBarProps = {
    multiDateRangeType: 'multiDateRangeType',
    hasChanged: 'hasChanged',
    regionList: 'regionList',
    selectedRegion: 'selectedRegion',
    allOffices: 'allOffices',
    officesMap: 'officesMap',
    officesByRegion: 'officesByRegion',
    selectedOffice: 'selectedOffice',
    selectedStartIso: 'selectedStartIso',
    selectedEndIso: 'selectedEndIso',
    carNumMap: 'carNumMap',
    selectedCarNum: 'selectedCarNum',
    privateCarNum: 'privateCarNum',
    carDriverInOfficeMap: 'carDriverInOfficeMap',
    carDriverInPeriodList: 'carDriverInPeriodList',
    selectedCarDriver: 'selectedCarDriver',
    limitDateList: 'limitDateList',
    selectedLimitDate: 'selectedLimitDate',
    selectedPayType: 'selectedPayType',
    selectByMonth: 'selectByMonth',
    timeList: 'timeList',
    settings: 'settings',
    selectedCarTypeGroup: 'selectedCarTypeGroup',
    selectedArea: 'selectedArea',
};

const defaultSettings = {
    autoSelectDriver: false,
    showPayType: false,
    secondaryStyle: false,
    strictHourByOffice: false,
    syncTime: false,
};

export const allTimeList = Array.from({ length: 24 }, (_, index) => index.toString().padStart(2,'0'));
const allCarNumOpt = {id: null, label: '전체'};
export const allCarDriverOpt = '전체';

function getInitialSearchBar() {
    return {
        [searchBarProps.selectedCarTypeGroup]: null,
        [searchBarProps.selectedArea]: null,
        [searchBarProps.multiDateRangeType]: multiDateRangeTypes.day,
        [searchBarProps.hasChanged]: false,
        [searchBarProps.regionList]: [],
        [searchBarProps.selectedRegion]: null, // string
        [searchBarProps.allOffices]: [],
        [searchBarProps.officesMap]: {}, // {[officeId]: {id: null, label: ''}}
        [searchBarProps.officesByRegion]: {}, // {[regionName]: {id: null, label: ''}[]}
        [searchBarProps.selectedOffice]: null,
        [searchBarProps.selectedStartIso]: startDateInit(1),
        [searchBarProps.selectedEndIso]: endDateInit(),
        [searchBarProps.carNumMap]: {}, // {[officeId]: {id: null, label: ''}[]}
        [searchBarProps.selectedCarNum]: allCarNumOpt,
        [searchBarProps.privateCarNum]: '',
        [searchBarProps.carDriverInOfficeMap]: {}, // {[officeId]: string[]}
        [searchBarProps.carDriverInPeriodList]: [],
        [searchBarProps.selectedCarDriver]: allCarDriverOpt,
        [searchBarProps.limitDateList]: [],
        [searchBarProps.selectedLimitDate]: null,
        [searchBarProps.selectedPayType]: null,
        [searchBarProps.selectByMonth]: false,
        [searchBarProps.timeList]: allTimeList,
        [searchBarProps.settings]: {...defaultSettings},

        pendingCommits: [],
        pendingFetchs: [],
    };
}

export function createLimitDateList(selectedStartIso, selectedEndIso) {
    let groups = [];
    let currDayJS = dayjs(selectedStartIso);
    const endDayJS = dayjs(selectedEndIso);
    let startIso;
    let startDate;
    let endIso;
    let endDate;
    let currMonthId;
    let dateLabel;
    let dateLabelSuffixes;
    while (currDayJS.isBefore(endDayJS)) {
        startIso = currDayJS.format('YYYY-MM-DDTHH:mm:ss');
        startDate = currDayJS.format('YYYY-MM-DD');
        const curMonth = currDayJS.month() + 1 ;
        dateLabel = startDate;
        dateLabelSuffixes = [];
        currMonthId = currDayJS.format('YYYY-MM');
        currDayJS = currDayJS.add(1, 'day');
        if (currDayJS.isAfter(endDayJS)) {
            currDayJS = endDayJS;
        }

        if (dateLabelSuffixes.length > 0) {
            dateLabel += ` (${dateLabelSuffixes.join(' ')})`;
        }

        endIso = currDayJS.format('YYYY-MM-DDTHH:mm:ss');
        endDate = currDayJS.format('YYYY-MM-DD');

        if (groups.length === 0 || groups[groups.length - 1].id !== currMonthId) {
            groups.push({
                id: currMonthId,
                label: `${curMonth}월`,
                dateList: [],
            });
        }

        groups[groups.length - 1].dateList.push({
            label: dateLabel,
            monthId: currMonthId,
            startIso,
            endIso,
        });
    }

    return groups;
}

async function fetchAllOfficeList({api, sign}) {
    const queryParams = {
        params: {},
    };
    const entity = new Entity.organizationHeaders(sign.userInfo.userId);
    const res = await axios.$get(
        `${api.operationUrl}partner/auth/${sign.userInfo.partnerAuthGroupId}`,
        queryParams,
        entity.get(),
    );

    const officeNameMap = {};
    const officeList = res.data.data.partnerList.map(item => {
        const office = {
            id: item.officeId,
            name: item.name,
            displayName: item.name,
            disassembledName: disassemble(item.name),
            area: item.area,
            shiftStartHour: parseInt(item.setTime.substring(0, 2)),
            dttiProp: item.settleType === 'DRIVE' ? 'stDtti' : 'edDtti',
        };

        if (!officeNameMap[item.name]) {
            officeNameMap[item.name] = [];
        }
        officeNameMap[item.name].push(office);

        return office;
    });
    // Handle duplicate office names
    officeList.forEach((office) => {
        if (officeNameMap[office.name].length > 1) {
            office.displayName = `[${office.area}] ${office.name}`;
            office.disassembledName = disassemble(office.displayName);
        }
    })
    officeList.sort((a, b) => a.name.localeCompare(b.name));

    return officeList;
}

async function fetchCarNumList({searchBar, api, sign}) {
    const entity = new Entity.organizationHeaders(sign.userInfo.userId);
    const params = {officeId: searchBar.selectedOffice.id};
    const res = await axios.$get(`${tachoEntity.baseUrl}driving/car-num/all`, {params}, entity.get());
    const carNumList = [allCarNumOpt];
    res.data.data.forEach(item => {
        carNumList.push({id: item, label: item});
    });

    return carNumList;
}

async function fetchCarDriverListInOffice({searchBar, api, sign}) {
    const entity = new Entity.organizationHeaders(sign.userInfo.userId);
    const params = {
        companyRegnum: searchBar.selectedOffice.id,
        page: 0,
        size: 3000,
    }
    const res = await axios.$get(`${tachoEntity.baseUrl}employee-info/list`, {params}, entity.get());
    const carDriverList = [allCarDriverOpt];
    const numMap = {};
    res.data.data.content.forEach(item => {
        numMap[item.employeeNum] = item;
    });
    Object.keys(numMap).sort().forEach(key => {
        const item = numMap[key];
        carDriverList.push(item.employeeNum);
    });

    return carDriverList;
}

let lastFetchCarDriverListInPeriod = {
    key: null,
    value: null,
};
async function fetchCarDriverListInPeriod({searchBar, api, sign}) {
    const entity = new Entity.organizationHeaders(sign.userInfo.userId);
    const params = {
        officeId: searchBar.selectedOffice.id,
        carNum: searchBar.selectedCarNum.id,
        startDate: changeFormat(searchBar.selectedStartIso, 'YYYYMMDDHHmmss'),
        endDate: changeFormat(searchBar.selectedEndIso, 'YYYYMMDDHHmmss')
    }
    const fetchKey = JSON.stringify(params);
    if (fetchKey === lastFetchCarDriverListInPeriod.key) {
        return lastFetchCarDriverListInPeriod.value;
    }

    const res = await axios.$get(`${tachoEntity.baseUrl}driving/driver-id/all`, {params}, entity.get());
    const carDriverList = [];
    res.data.data.forEach(item => {
        carDriverList.push(item);
    });

    lastFetchCarDriverListInPeriod.key = fetchKey;
    lastFetchCarDriverListInPeriod.value = carDriverList;
    return carDriverList;
}

export default {
    searchBarProps,
    allCarDriverOpt,
    getInitialSearchBar,
    actions: {
        async resetSearchBar({ commit, dispatch }) {
            commit('resetSearchBar');
            return dispatch('initSearchBar');
        },
        async initSearchBar(
            { state, commit, dispatch },
            settings = {},
        ) {
            const {searchBar, api, sign} = state;
            const mergedSettings = {
                ...defaultSettings,
                ...settings,
            };
            await dispatch('patchSearchBar', {prop: searchBarProps.settings, value: mergedSettings});
            if (searchBar.selectByMonth !== false) {
                await dispatch('patchSearchBar', {prop: searchBarProps.selectByMonth, value: false});
            }

            if (searchBar.regionList.length > 0) {
                await dispatch('patchSearchBar', {prop: 'resetTimes'});
                if (
                    settings.autoSelectDriver
                    && (searchBar.selectedCarNum === null || searchBar.selectedCarNum.id === null)
                    && (searchBar.carNumMap[searchBar.selectedOffice.id] && searchBar.carNumMap[searchBar.selectedOffice.id].length > 1)
                ) {
                    await dispatch('patchSearchBar', {prop: searchBarProps.selectedCarNum, value: searchBar.carNumMap[searchBar.selectedOffice.id][1]});
                }
                return
            }


            try {
                commit('setLoading', true);
                const allOfficeList = await fetchAllOfficeList({api, sign})
                await dispatch('patchSearchBar', {prop: searchBarProps.allOffices, value: allOfficeList});
            } catch (err) {
                commit('openMessageBox', err);
                return;
            } finally {
                commit('setLoading', false);
            }
        },
        async patchSearchBar({ state, commit }, {prop, value, extra}) {
            await commit('updateSearchBarProp', {prop, value, extra});
            let shouldStopLoading = false;
            while (state.searchBar.pendingCommits.length > 0 || state.searchBar.pendingFetchs.length > 0) {
                if (state.searchBar.pendingFetchs.length > 0) {
                    commit('setLoading', true);
                    try {
                        const resp = await state.searchBar.pendingFetchs.shift()();
                        if (resp) {
                            const {prop, value, extra} = resp;
                            commit('updateSearchBarProp', {prop, value, extra});
                        }
                    } catch (e) {
                        commit('openMessageBox', e);
                    } finally {
                        shouldStopLoading = true;
                    }
                    continue;
                }

                const {prop, value} = state.searchBar.pendingCommits.shift();
                commit('updateSearchBarProp', {prop, value, extra});

                if (shouldStopLoading) {
                    commit('setLoading', false);
                }
            }
        },
    },
    mutations: {
        selectLimitDate(state, value) {
            state.searchBar.selectedLimitDate = value;
        },
        updateDateRangeGroup(state) {
            const {searchBar} = state;
            const limitDateList = createLimitDateList(searchBar.selectedStartIso, searchBar.selectedEndIso);
            state.searchBar.limitDateList = limitDateList;
            state.searchBar.selectedLimitDate = limitDateList.length > 0
                ? limitDateList[0].dateList[0]
                : null;
        },
        resetSearchBar(state) {
            state.searchBar = getInitialSearchBar();
        },
        updateSearchBarProp(state, {prop, value, extra}) {
            const {searchBar} = state;
            switch (prop) {
                case 'dateRange': {
                    searchBar.hasChanged = true;
                    searchBar.selectedStartIso = value[searchBarProps.selectedStartIso];
                    searchBar.selectedEndIso = value[searchBarProps.selectedEndIso];

                    // Update car driver list:start
                    if (
                        searchBar.selectedOffice === null
                        || searchBar.selectedCarNum.id === null
                        || searchBar.settings.autoSelectDriver === false
                    ) {
                        return;
                    }

                    searchBar.pendingFetchs.push(async () => {
                        const carDriverList = await fetchCarDriverListInPeriod(state);
                        return {
                            prop: searchBarProps.carDriverInPeriodList,
                            value: carDriverList,
                        };
                    });
                    // Update car driver list:end
                    break;
                }
                case 'resetTimes': {
                    let startIso = startDateInit(1);

                    if (searchBar.selectByMonth) {
                        startIso = dayjs(startIso).startOf('month');
                    }

                    if (searchBar.selectedOffice) {
                        const officeHours = getOfficeShiftStartHours(searchBar.selectedOffice);
                        const recommendedOfficeHourStr = officeHours[officeHours.length - 1].toString().padStart(2,'0');

                        startIso = `${changeFormat(startIso, 'YYYY-MM-DD')}T${recommendedOfficeHourStr}:00:00`;
                    }

                    state.searchBar.pendingCommits.push({
                        prop: searchBarProps.selectedStartIso,
                        value: startIso,
                    });
                    break;
                }
                case searchBarProps.selectedStartIso:
                case searchBarProps.selectedEndIso: {
                    if (searchBar[prop] !== value) {
                        searchBar.hasChanged = true;
                    }
                    searchBar[prop] = value;

                    if (prop === searchBarProps.selectedStartIso) {
                        const endJS = searchBar.selectByMonth
                            ? dayjs(value).add(1, 'month')
                            : dayjs(value).add(1, 'day');
                        state.searchBar.selectedEndIso = endJS.format('YYYY-MM-DDTHH:mm:ss');
                    } else if (searchBar.settings.syncTime) {
                        const startJS = dayjs(searchBar.selectedStartIso);
                        const endJS = dayjs(searchBar.selectedEndIso);
                        const nextStartJS = startJS.hour(endJS.hour());
                        searchBar.selectedStartIso = nextStartJS.format('YYYY-MM-DDTHH:mm:ss');
                    }

                    // Update car driver list:start
                    if (
                        searchBar.selectedOffice === null
                        || searchBar.selectedCarNum.id === null
                        || searchBar.settings.autoSelectDriver === false
                    ) {
                        return;
                    }

                    searchBar.pendingFetchs.push(async () => {
                        const carDriverList = await fetchCarDriverListInPeriod(state);
                        return {
                            prop: searchBarProps.carDriverInPeriodList,
                            value: carDriverList,
                        };
                    });
                    // Update car driver list:end
                    break;
                }
                case searchBarProps.hasChanged: {
                    searchBar.hasChanged = value;
                    break;
                }
                case searchBarProps.carDriverInPeriodList: {
                    searchBar.carDriverInPeriodList = value;

                    let nextSelectedCarDriver = allCarDriverOpt;
                    if (searchBar.settings.autoSelectDriver && value.length > 0) {
                        nextSelectedCarDriver = value[0];
                    }
                    searchBar.pendingCommits.push({
                        prop: searchBarProps.selectedCarDriver,
                        value: nextSelectedCarDriver,
                    });
                    break;
                }
                case searchBarProps.carDriverInOfficeMap: {
                    searchBar.carDriverInOfficeMap = value;
                    break;
                }
                case searchBarProps.selectedCarNum: {
                    if (searchBar[prop] !== value) {
                        searchBar.hasChanged = true;
                    }
                    searchBar.selectedCarNum = value;

                    // Update car driver list:start
                    if (
                        value === null
                        || value.id === null
                        || searchBar.settings.autoSelectDriver === false
                    ) {
                        return;
                    }

                    searchBar.pendingFetchs.push(async () => {
                        const carDriverList = await fetchCarDriverListInPeriod(state);
                        return {
                            prop: searchBarProps.carDriverInPeriodList,
                            value: carDriverList,
                        };
                    });
                    // Update car driver list:end
                    break;
                }
                case searchBarProps.carNumMap: {
                    searchBar.carNumMap = value;

                    let nextSelectedCarNum = allCarNumOpt;
                    if (searchBar.settings.autoSelectDriver) {
                        const {
                            selectedOffice,
                            carNumMap,
                        } = searchBar;

                        if (
                            selectedOffice !== null
                            && carNumMap[selectedOffice.id]
                            && carNumMap[selectedOffice.id].length > 1
                        ) {
                            nextSelectedCarNum = carNumMap[selectedOffice.id][1];
                        }
                    }

                    searchBar.pendingCommits.push({
                        prop: searchBarProps.selectedCarNum,
                        value: nextSelectedCarNum,
                    });
                    break;
                }
                case searchBarProps.selectedOffice: {
                    if (searchBar[prop] !== value) {
                        searchBar.hasChanged = true;
                    }
                    searchBar.selectedOffice = value;
                    if (value === null || value.id === null) {
                        return;
                    }

                    try {
                        localStorage.setItem('lastSelectedOfficeId', value.id);
                    } catch (e) {
                        // Ignore localStorage errors
                    }

                    const officeHours = getOfficeShiftStartHours(searchBar.selectedOffice);
                    if (searchBar.settings.strictHourByOffice) {
                        searchBar.timeList = officeHours.map(hour => hour.toString().padStart(2, '0'));
                    }
                    searchBar.pendingCommits.push({
                        prop: 'resetTimes',
                    });

                    if (searchBar.carNumMap[value.id]) {
                        let nextCarNum = allCarNumOpt;
                        if (
                            searchBar.settings.autoSelectDriver === true
                            && searchBar.carNumMap[value.id].length > 1
                        ) {
                            nextCarNum = searchBar.carNumMap[value.id][1];
                        }
                        searchBar.pendingCommits.push({
                            prop: searchBarProps.selectedCarNum,
                            value: nextCarNum,
                        });

                        if (searchBar.settings.autoSelectDriver === false) {
                            searchBar.pendingCommits.push({
                                prop: searchBarProps.selectedCarDriver,
                                value: allCarDriverOpt,
                            });
                        }
                        return;
                    }

                    searchBar.pendingFetchs.push(async () => {
                        const carDriverList = await fetchCarDriverListInOffice(state);
                        const nextCarDriverMap = {...searchBar.carDriverInOfficeMap};
                        nextCarDriverMap[value.id] = carDriverList;
                        return {
                            prop: searchBarProps.carDriverInOfficeMap,
                            value: nextCarDriverMap,
                        };
                    });

                    searchBar.pendingFetchs.push(async () => {
                        const carNumList = await fetchCarNumList(state);
                        const nextCarNumMap = {...searchBar.carNumMap};
                        nextCarNumMap[value.id] = carNumList;
                        return {
                            prop: searchBarProps.carNumMap,
                            value: nextCarNumMap,
                        };
                    });
                    break;
                }
                case searchBarProps.officesByRegion: {
                    searchBar.officesByRegion = value;
                    searchBar.selectedOffice = null;

                    if (searchBar.selectedRegion === null || !searchBar.officesByRegion[searchBar.selectedRegion]) {
                        return;
                    }

                    searchBar.pendingCommits.push({
                        prop: searchBarProps.selectedOffice,
                        value: searchBar.officesByRegion[searchBar.selectedRegion][0],
                    });
                    break;
                }
                // case searchBarProps.selectedRegion: {
                //     searchBar.selectedRegion = value;
                //     if (value === null) {
                //         return;
                //     }
                //
                //     if (searchBar.officesByRegion[value]) {
                //         searchBar.pendingCommits.push({
                //             prop: searchBarProps.selectedOffice,
                //             value: searchBar.officesByRegion[value][0],
                //         });
                //         return;
                //     }
                //     break;
                // }
                case searchBarProps.allOffices: {
                    searchBar.allOffices = value;
                    searchBar.officesMap = {};
                    searchBar.regionList = [];
                    searchBar.officesByRegion = {};

                    const regionSet = new Set();
                    searchBar.allOffices.forEach((office) => {
                        searchBar.officesMap[office.id] = office;
                        regionSet.add(office.area);
                        if (!searchBar.officesByRegion[office.area]) {
                            searchBar.officesByRegion[office.area] = [];
                        }
                        searchBar.officesByRegion[office.area].push(office);
                    });
                    if (searchBar.allOffices.length > 0) {
                        let selectedOffice = searchBar.allOffices[0];
                        try {
                            const lastSelectedOfficeId = localStorage.getItem('lastSelectedOfficeId');
                            if (lastSelectedOfficeId) {
                                const officeFromStorage = searchBar.allOffices.find(office => office.id === lastSelectedOfficeId);
                                if (officeFromStorage) {
                                    selectedOffice = officeFromStorage;
                                }
                            }
                        } catch (e) {
                            // Ignore localStorage errors
                        }
                        searchBar.pendingCommits.push({
                            prop: searchBarProps.selectedOffice,
                            value: selectedOffice,
                        });
                    }

                    searchBar.regionList = Array.from(regionSet).sort();
                    break;
                }
                // case searchBarProps.regionList: {
                //     searchBar.regionList = value;
                //     searchBar.selectedRegion = null;
                //     if (value.length > 0) {
                //         searchBar.pendingCommits.push({
                //             prop: searchBarProps.selectedRegion,
                //             value: value[0],
                //         });
                //     }
                //     break;
                // }
                case searchBarProps.selectByMonth: {
                    searchBar.selectByMonth = value;
                    searchBar.pendingCommits.push({
                        prop: 'resetTimes',
                    });
                    break;
                }
                case searchBarProps.settings: {
                    searchBar.settings = value;
                    if (!value.showPayType) {
                        searchBar.selectedPayType = null;
                    }
                    if (value.strictHourByOffice && searchBar.selectedOffice) {
                        searchBar.timeList = getOfficeShiftStartHours(searchBar.selectedOffice).map(hour => hour.toString().padStart(2, '0'));
                    } else {
                        searchBar.timeList = allTimeList;
                    }
                    break;
                }
                default:
                    searchBar[prop] = value;
                    break;
            }
        },
    },
};
